import {Injectable} from '@angular/core';
import moment from "moment";
import {HttpClient} from "@angular/common/http";
import {forkJoin, Observable, of} from "rxjs";
import {CacheService} from "@customcoding/shared";
import {catchError, map, tap} from "rxjs/operators";
import {Room, RoomConflict} from "../models/room";

export enum RoomAvailability {
	Used = 'used',
	Free = 'free'
}

@Injectable({
	providedIn: 'root'
})
export class RoomService {

	constructor(private http: HttpClient, private cache: CacheService) {
	}

	filterRooms(locationId: number, selectedDate: any): Observable<any[]> {
		if (selectedDate == null || locationId == null) {
			return of([]);
		}

		const formattedStartDate =
			moment(selectedDate.startDate).format('YYYY-MM-DD') +
			'-' +
			moment(selectedDate.startTime.hours + ':' + selectedDate.startTime.minutes, 'H:m').format('HH-mm');

		const formattedEndDate =
			moment(selectedDate.endDate).format('YYYY-MM-DD') +
			'-' +
			moment(selectedDate.endTime.hours + ':' + selectedDate.endTime.minutes, 'H:m').format('HH-mm');

		return this.getFilterRoomsObservable(locationId, [{startDate: formattedStartDate, endDate: formattedEndDate}])
			.pipe(
				map(results => {
					if (results[0]?.rooms) {
						return results[0]?.rooms
					} else {
						return [];
					}
				}),
				catchError(err => {
					MoConsole.error(err);
					return of([]);
				}))
	}

	filterRoomsMultiDate(locationId: number, selectedDates: any[]): Observable<any[]> {
		// note that unlike filterRooms, this function will only return rooms that are available for all dates. It won't just mark them as "used"
		if (!selectedDates?.length || locationId == null) {
			return of([]);
		}
		const formattedDates = selectedDates.map(selectedDate => {
			return {
				startDate:
					moment(selectedDate.startDate, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD') +
					'-' +
					moment(selectedDate.startDate, 'YYYY-MM-DD HH:mm').format('HH-mm'),
				endDate:
					moment(selectedDate.endDate, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD') +
					'-' +
					moment(selectedDate.endDate, 'H:m').format('HH-mm')
			}
		});

		return this.getFilterRoomsObservable(locationId, formattedDates).pipe(
			map(responses => {
				if (!responses || responses.length === 0) {
					return [];
				}

				const roomMap = new Map<number, { room: Room, occurrences: number }>();

				// count the occurrences of each room being free across all dates
				responses.forEach(({rooms}) => {
					rooms.forEach(room => {
						if (room.status === RoomAvailability.Free) {
							if (roomMap.has(room.id)) {
								roomMap.get(room.id).occurrences += 1;
							} else {
								roomMap.set(room.id, {room: room, occurrences: 1});
							}
						}
					});
				});
				// filter rooms that are free for all dates
				const freeRooms = Array.from(roomMap.values())
					.filter(room => room.occurrences === selectedDates.length)
					.map(room => room.room);
				return freeRooms;
			}),
			catchError(err => {
				MoConsole.error(err);
				return of([]);
			})
		);
	}

	separateDateAndTime(selectedDate: any) {
		let startDate = moment(selectedDate.startDate, 'YYYY-MM-DD HH:mm');
		let endDate = moment(selectedDate.endDate, 'YYYY-MM-DD HH:mm');
		let startTime = {hours: startDate.format('HH'), minutes: startDate.format('mm')};
		let endTime = {hours: endDate.format('HH'), minutes: endDate.format('mm')};
		return {
			...selectedDate,
			startTime: startTime,
			endTime: endTime,
		}
	}

	filterRoomsForOteam(locationId: number, date: any, time: string, endTime: string) {
		// const selectedDate = this.formGroup.value.datepicker;
		if (date == null || locationId == null) {
			// this.filteredRooms = [];
			return of([]);
		}

		const formattedStartDate =
			moment(date).format('YYYY-MM-DD') +
			'-' +
			moment(time, 'H:m').format('HH-mm');

		const formattedEndDate =
			moment(date).format('YYYY-MM-DD') +
			'-' +
			moment(endTime, 'H:m').format('HH-mm');

		return this.getFilterRoomsObservable(locationId, [{startDate: formattedStartDate, endDate: formattedEndDate}])
			.pipe(
				map(results => {
					if (results[0]?.rooms) {
						return results[0]?.rooms
					} else {
						return [];
					}
				}),
				catchError(err => {
					MoConsole.error(err);
					return of([]);
				}));
	}

	getFilterRoomsObservable(locationId: any, formattedDates: { startDate: string; endDate: string }[]) {
		let appointmentUrlPart = '';
		formattedDates.forEach((date, index) => {
			appointmentUrlPart += `&appointments[${index}][startDate]=${date.startDate}`;
			appointmentUrlPart += `&appointments[${index}][endDate]=${date.endDate}`;
		})
		if (!locationId || locationId == 'isExtern') {
			// substring the first '&' at the start of the url and replace it with a '?'
			appointmentUrlPart = '?' + appointmentUrlPart.substring(1);
			return this.http.get<any[]>(`/api/scap/rooms/availability${appointmentUrlPart}`);
		}
		return this.http.get<any[]>(`/api/scap/rooms/availability?location_id=${locationId}${appointmentUrlPart}`);
	}

	getReservations(payload: any) {
		const {type, rooms, date} = payload;
		const params = new URLSearchParams();
		params.append('type', type);
		params.append('date', date);
		rooms.forEach(room => {
			params.append('rooms[]', room);
		});
		const url = `/api/scap/rooms/reservations?${params}`;
		return this.http.get(url);
	}

	allRooms() {
		return this.http.get<any[]>('/api/scap/room');
	}

	allRoomsCached() {
		return this.cache.get<any[]>('/api/scap/room');
	}

	getConflictsDP(roomID: number, locationID: number, dates: any[], appointmentId: number = null) {
		const newDates = dates.map(date => {
			return {
				startDate: moment(date.startDate, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD') + ' ' + date.startTime.hours + ':' + date.startTime.minutes,
				endDate: moment(date.endDate, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD') + ' ' + date.endTime.hours + ':' + date.endTime.minutes
			}
		})
		return this.getConflicts(roomID, locationID, newDates, appointmentId);
	}

	getConflicts(
		roomID: number,
		locationID: number,
		dates: {
			startDate: string,
			endDate: string
		}[],
		appointmentId: number = null
	): Promise<RoomConflict[]> {
		MoConsole.log('dates',dates)
		return new Promise((resolve, reject) => {
			let roomConflicts = [];
			let formattedDates = [];
			dates.forEach(date => {
				let startDate = moment(date.startDate, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD');
				let endDate = moment(date.endDate, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD');
				let startTime = moment(date.startDate, 'YYYY-MM-DD HH:mm').format('HH-mm');
				let endTime = moment(date.endDate, 'YYYY-MM-DD HH:mm').format('HH-mm');
				formattedDates.push({
					startDate: startDate + '-' + startTime,
					endDate: endDate + '-' + endTime,
				});
			})

			this.getFilterRoomsObservable(locationID, formattedDates).subscribe({
				next: (results: any[]) => {
					results.forEach((result, index) => {
						const unavailableRooms = result.rooms.filter(room => {
							 return room.status === RoomAvailability.Used && !room.conflictAppointmentIds?.includes(appointmentId)
						});
						const availableRooms = result.rooms.filter(room => {
							return room.status === RoomAvailability.Free || (room.status === RoomAvailability.Used && room.conflictAppointmentIds?.includes(appointmentId))
						});
						const roomIsUnavailable = unavailableRooms.find(room => room.id == roomID);
						if (roomIsUnavailable) {
							roomConflicts.push({
								unavailableRoom: roomIsUnavailable,
								availableRooms: availableRooms,
								affectedDate: dates[index]
							});
						}
					});
					resolve(roomConflicts);
				},
				error: (error) => {
					MoConsole.log('failed to retrieve filtered rooms', error);
					reject(error);
				}
			});
		});
	}
}
